// ============================================================================
// LorenZone - Client API pour l'Edge Function "lz-api"
// ----------------------------------------------------------------------------
// Remplace les appels directs au SDK Supabase côté front.
// Toutes les opérations passent désormais par https://.../functions/v1/lz-api
// qui utilise la clé service_role côté serveur (jamais exposée au client).
//
// Usage :
//   await lzApi.collectionLoad({ email, universe: 'pkm' })
//   await lzApi.toggleProduct({ email, universe, productId, owned, seriesHandle, productTitle })
//   await lzApi.toggleVariant({ email, universe, variantId, owned, variantKind, seriesHandle, productTitle })
//   await lzApi.syncProgress({ email, universe, seriesHandle, seriesTitle, totalCards, ownedCards, totalReverseCards, ownedReverseCards })
//   await lzApi.favoritesList({ email })
//   await lzApi.favoritesToggle({ email, universe, seriesHandle, favorite })
//   await lzApi.preferencesGet({ email })
//   await lzApi.preferencesSet({ email, favoriteLicense })
//   await lzApi.activityLog({ email })
//   await lzApi.progressGetAll({ email })
//   await lzApi.ping({ email })
//
// Toutes les méthodes renvoient { ok: true, data: ... } ou { ok: false, error: '...' }
// ============================================================================

(function () {
  if (window.lzApi) return; // idempotent — pas d'init multiple

  // URL de l'Edge Function.
  // `window.LZ_SUPABASE_URL` est défini par Liquid dans layout/theme.liquid.
  const BASE_URL =
    (window.LZ_SUPABASE_URL || 'https://tjsehkvwematlyupthzl.supabase.co').replace(/\/$/, '') +
    '/functions/v1/lz-api';

  // Clé publique anon — requise par Supabase (vérification JWT basique avant
  // d'entrer dans notre code d'Edge Function). Elle est déjà publique dans
  // layout/theme.liquid donc la réutiliser ici ne change rien niveau sécurité.
  const ANON_KEY = window.LZ_SUPABASE_ANON_KEY || '';

  /**
   * Envoi bas-niveau à l'Edge Function.
   * @param {string} action
   * @param {string} email
   * @param {object} [payload]
   * @returns {Promise<{ok: boolean, data?: any, error?: string, detail?: any}>}
   */
  async function call(action, email, payload) {
    if (!ANON_KEY) {
      console.error('[lzApi] LZ_SUPABASE_ANON_KEY manquante (layout/theme.liquid)');
      return { ok: false, error: 'missing_anon_key' };
    }
    if (!email) {
      return { ok: false, error: 'missing_email' };
    }
    try {
      const res = await fetch(BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + ANON_KEY,
          apikey: ANON_KEY,
        },
        body: JSON.stringify({ action, email, payload: payload || {} }),
        credentials: 'omit',
      });
      let body;
      try {
        body = await res.json();
      } catch {
        body = { ok: false, error: 'invalid_json_response' };
      }
      if (!res.ok && body && body.ok !== false) {
        body = { ok: false, error: 'http_' + res.status };
      }
      return body;
    } catch (err) {
      console.error('[lzApi] network error for action', action, err);
      return { ok: false, error: 'network_error', detail: String(err) };
    }
  }

  // ------------------------------------------------------------
  // API publique
  // ------------------------------------------------------------

  const lzApi = {
    /** Ping / health check — ne touche pas la DB. */
    ping(opts) {
      return call('ping', opts.email, {});
    },

    /**
     * Charge les shopify_product_id et shopify_variant_id possédés par un user
     * pour un universe donné.
     * @param {{email: string, universe: 'pkm'|'op'|'lor'|'naruto'}} opts
     * @returns {Promise<{ok:true, data:{products:string[], variants:string[]}}|{ok:false,error:string}>}
     */
    collectionLoad(opts) {
      return call('collection.load', opts.email, { universe: opts.universe });
    },

    /**
     * Ajoute ou retire un product de la collection d'un user.
     * @param {{email:string, universe:string, productId:string|number, owned:boolean,
     *          seriesHandle?:string, productTitle?:string}} opts
     */
    toggleProduct(opts) {
      return call('collection.toggleProduct', opts.email, {
        universe: opts.universe,
        product_id: opts.productId,
        owned: !!opts.owned,
        series_handle: opts.seriesHandle || '',
        product_title: opts.productTitle || '',
      });
    },

    /**
     * Ajoute ou retire une variant.
     * @param {{email:string, universe:string, variantId:string|number, owned:boolean,
     *          variantKind?:string, seriesHandle?:string, productTitle?:string}} opts
     */
    toggleVariant(opts) {
      return call('collection.toggleVariant', opts.email, {
        universe: opts.universe,
        variant_id: opts.variantId,
        owned: !!opts.owned,
        variant_kind: opts.variantKind || '',
        series_handle: opts.seriesHandle || '',
        product_title: opts.productTitle || '',
      });
    },

    /**
     * Synchronise la progression d'une série (upsert user_series_progress).
     * @param {{email:string, universe:string, seriesHandle:string, seriesTitle?:string,
     *          totalCards?:number, ownedCards?:number, totalReverseCards?:number, ownedReverseCards?:number}} opts
     */
    syncProgress(opts) {
      return call('collection.syncProgress', opts.email, {
        universe: opts.universe,
        series_handle: opts.seriesHandle,
        series_title: opts.seriesTitle || '',
        total_cards: opts.totalCards || 0,
        owned_cards: opts.ownedCards || 0,
        total_reverse_cards: opts.totalReverseCards || 0,
        owned_reverse_cards: opts.ownedReverseCards || 0,
      });
    },

    /**
     * Liste les séries favorites d'un user.
     * @param {{email:string}} opts
     * @returns {Promise<{ok:true, data:{favorites:Array<{universe:string, series_handle:string, created_at:string}>}}|{ok:false,error:string}>}
     */
    favoritesList(opts) {
      return call('favorites.list', opts.email, {});
    },

    /**
     * Ajoute ou retire une série favorite.
     * @param {{email:string, universe:string, seriesHandle:string, favorite:boolean}} opts
     */
    favoritesToggle(opts) {
      return call('favorites.toggle', opts.email, {
        universe: opts.universe,
        series_handle: opts.seriesHandle,
        favorite: !!opts.favorite,
      });
    },

    /**
     * Récupère les préférences d'un user (ex: favorite_license).
     * @param {{email:string}} opts
     */
    preferencesGet(opts) {
      return call('preferences.get', opts.email, {});
    },

    /**
     * Définit les préférences d'un user.
     * @param {{email:string, favoriteLicense:'pkm'|'op'|'lor'|'naruto'}} opts
     */
    preferencesSet(opts) {
      return call('preferences.set', opts.email, {
        favorite_license: opts.favoriteLicense,
      });
    },

    /**
     * Log la visite du jour (first visit + today) — équivalent de logUserDailyActivity.
     * @param {{email:string}} opts
     */
    activityLog(opts) {
      return call('activity.log', opts.email, {});
    },

    /**
     * Récupère tous les progress d'un user (toutes séries, tous univers).
     * Utilisé par la page /pages/ma-collection.
     * @param {{email:string}} opts
     */
    progressGetAll(opts) {
      return call('progress.getAll', opts.email, {});
    },

    /** @internal Exposé pour debug console. */
    _call: call,
    _baseUrl: BASE_URL,
  };

  window.lzApi = lzApi;
})();
